'use client';

import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import Image from 'next/image';
import { X, Calendar } from 'lucide-react';

const FALLBACK_IMAGE = '/assets/backgrounds/dotcms-demo-bg.svg';
const PAINT_OFFSET = 300;

export function DemoModal({ frameCode, previewImage, onClose, onScheduleDemo }) {
    const contentRef = useRef(null);
    const closeButtonRef = useRef(null);
    const [visible, setVisible] = useState(false);
    const [iframeReady, setIframeReady] = useState(false);

    useEffect(() => {
        const raf = requestAnimationFrame(() => {
            setVisible(true);
            closeButtonRef.current?.focus();
        });
        document.body.style.overflow = 'hidden';
        return () => {
            cancelAnimationFrame(raf);
            document.body.style.overflow = '';
        };
    }, []);

    useEffect(() => {
        const container = contentRef.current;
        if (!container || !frameCode) return;
        container.innerHTML = frameCode;

        let listenerCleanup = () => {};
        const fallback = setTimeout(() => setIframeReady(true), 5000);

        const attachToIframe = (iframe) => {
            if (iframe.contentDocument?.readyState === 'complete') {
                setTimeout(() => setIframeReady(true), PAINT_OFFSET);
                return;
            }
            const onLoaded = () => setTimeout(() => setIframeReady(true), PAINT_OFFSET);
            iframe.addEventListener('load', onLoaded);
            listenerCleanup = () => iframe.removeEventListener('load', onLoaded);
        };

        const iframe = container.querySelector('iframe');
        if (iframe) {
            attachToIframe(iframe);
        } else {
            const observer = new MutationObserver(() => {
                const injected = container.querySelector('iframe');
                if (injected) {
                    observer.disconnect();
                    attachToIframe(injected);
                }
            });
            observer.observe(container, { childList: true, subtree: true });
            listenerCleanup = () => observer.disconnect();
        }

        return () => {
            listenerCleanup();
            clearTimeout(fallback);
        };
    }, [frameCode]);

    useEffect(() => {
        const onKey = (e) => {
            if (e.key === 'Escape') handleClose();
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    });

    const handleClose = () => {
        setVisible(false);
        setTimeout(onClose, 300);
    };

    const handleScheduleDemo = () => {
        setVisible(false);
        setTimeout(() => {
            onClose();
            onScheduleDemo?.();
        }, 300);
    };

    return createPortal(
        <div
            onClick={(e) => {
                if (e.target === e.currentTarget) handleClose();
            }}
            style={{
                position: 'fixed',
                inset: 0,
                zIndex: 9999,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: visible ? 'rgba(0,0,0,0.85)' : 'rgba(0,0,0,0)',
                transition: 'background-color 300ms ease'
            }}>
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    width: '90vw',
                    height: '90vh',
                    transform: visible ? 'scale(1)' : 'scale(0.88)',
                    opacity: visible ? 1 : 0,
                    transition: 'transform 400ms cubic-bezier(0.16,1,0.3,1), opacity 300ms ease'
                }}>
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: '8px',
                        paddingBottom: '10px'
                    }}>
                    {onScheduleDemo && (
                        <button
                            onClick={handleScheduleDemo}
                            style={{
                                height: '36px',
                                padding: '0 14px',
                                borderRadius: '8px',
                                border: 'none',
                                background: 'rgba(182,38,232,0.95)',
                                color: 'white',
                                cursor: 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px',
                                fontSize: '13px',
                                fontWeight: 600,
                                letterSpacing: '0.01em',
                                transition: 'background-color 200ms ease, transform 200ms ease'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.background = 'rgba(182,38,232,1)';
                                e.currentTarget.style.transform = 'translateY(-1px)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'rgba(182,38,232,0.95)';
                                e.currentTarget.style.transform = 'translateY(0)';
                            }}>
                            <Calendar size={14} />
                            Schedule a Demo
                        </button>
                    )}
                    <button
                        ref={closeButtonRef}
                        onClick={handleClose}
                        aria-label="Close"
                        style={{
                            width: '36px',
                            height: '36px',
                            borderRadius: '50%',
                            border: 'none',
                            background: 'rgba(255,255,255,0.15)',
                            color: 'white',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backdropFilter: 'blur(4px)'
                        }}>
                        <X size={18} />
                    </button>
                </div>

                <div
                    style={{
                        position: 'relative',
                        flex: 1,
                        borderRadius: '16px',
                        overflow: 'hidden',
                        background: '#0C1231',
                        boxShadow: '0 25px 80px rgba(0,0,0,0.6)',
                        minHeight: 0
                    }}>
                    <div
                        ref={contentRef}
                        style={{ width: '100%', height: '100%' }}
                        className="[&_iframe]:block [&_iframe]:size-full [&_iframe]:border-none"
                    />

                    <div
                        style={{
                            position: 'absolute',
                            inset: 0,
                            zIndex: 5,
                            opacity: iframeReady ? 0 : 1,
                            pointerEvents: iframeReady ? 'none' : 'auto',
                            transition:
                                'opacity 800ms ease-in-out, filter 500ms ease-in-out, transform 250ms ease-in-out',
                            filter: iframeReady ? 'blur(10px)' : 'blur(0px)',
                            transform: iframeReady ? 'scale(1.04)' : 'scale(1)'
                        }}>
                        <Image
                            src={previewImage || FALLBACK_IMAGE}
                            alt="Demo preview"
                            fill
                            className="object-cover object-left-top"
                            priority
                        />
                        <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/50" />
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
}
